import { useState, useEffect, useRef } from 'react';
import { fetchAvatars, submitVote, getVotedAvatars } from '../utils/api';
import {
  getSession,
  spendTokens,
  useFarmBoost,
  hasFarmBoost,
} from '../utils/session';
import {
  calculateAuraStats,
  getSwipeHypeMessage,
  formatAuraDisplay,
  getAuraBadge,
} from '../utils/aura';

const MID_WORDS = [
  'Skibidi',
  'Brain Rot',
  'Chopped',
  'Delulu',
  'Sus',
  'Salty',
  'Unc',
  'Clapped',
  'Noob',
  'Ick',
  'Dog Water',
  'Cap',
  'Opp',
  'Glaze',
  'Crash Out',
  'L',
  'Basic',
  'Extra',
  'Rage Bait',
  'Red Flag',
];

const FIRE_WORDS = [
  'Fire',
  'Lit',
  'Slay',
  "Bussin'",
  'Dope',
  'Fresh',
  'Clean',
  'Sauce',
  'Flex',
  'Ate',
  'Hard',
  'Drippy',
  'Iced Out',
  'Swag',
  'Fly',
  'Sharp',
  'On Point',
  'Gucci',
  'Peak',
  'Elite',
];

const getRandomMidWord = () => {
  return MID_WORDS[Math.floor(Math.random() * MID_WORDS.length)];
};

const getRandomFireWord = () => {
  return FIRE_WORDS[Math.floor(Math.random() * FIRE_WORDS.length)];
};

function AuraFarming({ session, onVoteComplete, onNavigate }) {
  const [avatars, setAvatars] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [swipeAnimation, setSwipeAnimation] = useState('');
  const [votingInProgress, setVotingInProgress] = useState(false);
  const [hypeMessage, setHypeMessage] = useState('');
  const [showHype, setShowHype] = useState(false);
  const [midWord, setMidWord] = useState(getRandomMidWord());
  const [fireWord, setFireWord] = useState(getRandomFireWord());
  const [currentTokens, setCurrentTokens] = useState(session?.tokens || 0);
  const [currentFarmBoost, setCurrentFarmBoost] = useState(session?.farmBoost || 0);
  const [rateLimitMessage, setRateLimitMessage] = useState('');
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const rateLimitRef = useRef(null);

  // Update local state when session prop changes
  useEffect(() => {
    setCurrentTokens(session?.tokens || 0);
    setCurrentFarmBoost(session?.farmBoost || 0);
  }, [session]);

  // Countdown timer for rate limit
  useEffect(() => {
    if (rateLimitCountdown > 0) {
      const timer = setTimeout(() => {
        setRateLimitCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (rateLimitCountdown === 0 && rateLimitMessage) {
      setRateLimitMessage('');
    }
  }, [rateLimitCountdown, rateLimitMessage]);

  // Scroll to rate limit message when it appears
  useEffect(() => {
    if (rateLimitMessage && rateLimitRef.current) {
      rateLimitRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [rateLimitMessage]);

  useEffect(() => {
    loadAvatars();
  }, []);

  // Generate new random words when avatar changes
  useEffect(() => {
    setMidWord(getRandomMidWord());
    setFireWord(getRandomFireWord());
  }, [currentIndex]);

  const loadAvatars = async () => {
    setLoading(true);
    try {
      const data = await fetchAvatars();

      // Get avatars user has already voted on from backend
      const { votedAvatarIds } = await getVotedAvatars();

      // Filter out avatars user has already swiped on
      const filteredData = data.filter(avatar => {
        return !votedAvatarIds.includes(avatar.id);
      });

      setAvatars(filteredData);
    } catch (err) {
      setError('Failed to load avatars');
      console.error('Load avatars error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (isFire) => {
    // Aura farming is now FREE - no cost check needed!
    if (votingInProgress) return;
    setVotingInProgress(true);

    const currentAvatar = avatars[currentIndex];

    try {
      console.log('⚡ Submitting swipe - backend will deduct 10 Lit-Bits');

      // Check and use Farm Boost
      const boostActive = hasFarmBoost();
      if (boostActive) {
        useFarmBoost(); // Decrement boost counter
        setCurrentFarmBoost(prev => prev - 1); // Update local state
      }

      // Calculate Aura stats for the current avatar (simulated before backend update)
      const fireSwipes = (currentAvatar.auraStats?.fireSwipes || 0) + (isFire ? 1 : 0);
      const leftSwipes = (currentAvatar.auraStats?.leftSwipes || 0) + (isFire ? 0 : 1);
      const auraStats = calculateAuraStats(fireSwipes, leftSwipes);

      // Get hype message (with boost indicator)
      let hype = getSwipeHypeMessage(isFire, auraStats);
      if (boostActive) {
        hype = `🚀 2X BOOST! ${hype}`;
      }
      setHypeMessage(hype);
      setShowHype(true);

      // Animate swipe
      setSwipeAnimation(isFire ? 'swipe-right' : 'swipe-left');

      // Submit vote (use 'aura_farming' as category for tracking)
      const result = await submitVote(currentAvatar.id, 'aura_farming', isFire);

      setTimeout(async () => {
        if (result.success) {
          console.log('✅ Swipe successful - balance:', result.balance);

          // Update balance immediately from vote response
          if (result.balance !== undefined) {
            const sessionData = getSession();
            if (sessionData) {
              sessionData.tokens = result.balance;
              localStorage.setItem('dripbattle_session', JSON.stringify(sessionData));
            }
            setCurrentTokens(result.balance);
            console.log('💰 Balance updated to:', result.balance);

            // Immediately update parent component's live balance
            await onVoteComplete(result.balance);
          } else {
            // Fallback if no balance in response
            await onVoteComplete();
          }

          // Hide notifications after delay
          setTimeout(() => {
            setShowHype(false);
          }, 2000);

          // Move to next avatar
          if (currentIndex < avatars.length - 1) {
            setCurrentIndex(currentIndex + 1);
            // New word will be generated by useEffect
          } else {
            // No more avatars
            setAvatars([]);
          }
        } else {
          console.error('❌ Swipe failed:', result);

          // Check if it's a rate limit error
          if (result.status === 429) {
            setRateLimitMessage('🚨 Whoa there! You\'re swiping too fast! The limit is 100 swipes per minute. Take a breather!');
            setRateLimitCountdown(60);
          } else {
            alert('Failed to submit swipe. Please try again.');
          }
        }

        setSwipeAnimation('');
        setVotingInProgress(false);
      }, 500);
    } catch (err) {
      console.error('💥 Swipe error:', err);

      // Check if it's a rate limit error
      if (err.response && err.response.status === 429) {
        setRateLimitMessage('🚨 Whoa there! You\'re swiping too fast! The limit is 100 swipes per minute. Take a breather!');
        setRateLimitCountdown(60);
      } else {
        alert('An error occurred while swiping.');
      }

      setSwipeAnimation('');
      setVotingInProgress(false);
    }
  };

  const handleKeyPress = (e) => {
    if (votingInProgress) return;
    if (e.key === 'ArrowLeft') handleSwipe(false); // Random bad word
    if (e.key === 'ArrowRight') handleSwipe(true); // Fire
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, votingInProgress]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner large"></div>
        <p>Loading avatars...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-text">{error}</p>
        <button onClick={loadAvatars} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  const currentAvatar = avatars[currentIndex];

  // Calculate Aura stats for display (from backend or simulated)
  const displayAuraStats = currentAvatar ? calculateAuraStats(
    currentAvatar.auraStats?.fireSwipes || 0,
    currentAvatar.auraStats?.leftSwipes || 0
  ) : null;

  const auraDisplay = displayAuraStats ? formatAuraDisplay(displayAuraStats) : null;
  const auraBadge = displayAuraStats ? getAuraBadge(displayAuraStats.auraPercent, displayAuraStats.totalSwipes) : null;

  return (
    <div className="aura-farming-container">
      <div className="aura-header">
        <h1 className="aura-title">⚡ Aura Farming ⚡</h1>
        <p className="aura-subtitle">Swipe to judge the drip!</p>

        {/* Farm Boost Indicator */}
        {currentFarmBoost > 0 && (
          <div className="farm-boost-active">
            🚀 FARM BOOST ACTIVE: {currentFarmBoost} swipes remaining (2x Aura!)
          </div>
        )}

        <p className="token-info" style={{fontSize: '1.2rem', color: '#00ff00', fontWeight: 'bold'}}>🎉 100% FREE - Swipe Forever! 🎉</p>
      </div>

      {/* Rate Limit Message */}
      {rateLimitMessage && (
        <div className="rate-limit-banner" ref={rateLimitRef}>
          <p className="rate-limit-message">{rateLimitMessage}</p>
          <p className="rate-limit-timer">
            ⏱️ You can swipe again in: <strong>{rateLimitCountdown}s</strong>
          </p>
        </div>
      )}

      {/* Hype Message Popup */}
      {showHype && (
        <div className={`hype-popup ${swipeAnimation.includes('right') ? 'fire' : 'mid'}`}>
          <div className="hype-text">{hypeMessage}</div>
        </div>
      )}

      <div className="swipe-area">
        {!currentAvatar ? (
          <div className="no-more-avatars">
            <h2>🎉 All Caught Up!</h2>
            <p>You've swiped on all available avatars.</p>
            <p>Check back later for more!</p>
            <button onClick={loadAvatars} className="reload-button">
              Refresh
            </button>
          </div>
        ) : (
          <>
            <div className={`swipe-card ${swipeAnimation}`}>
              <div
                className="swipe-image-container"
                onClick={() => onNavigate && onNavigate('profile', currentAvatar.username)}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={currentAvatar.avatarUrl}
                  alt={`${currentAvatar.username}'s avatar`}
                  className="swipe-image"
                />

                {/* Spotlight Badge */}
                {currentAvatar.isSpotlighted && (
                  <div className="spotlight-badge-overlay">
                    ⭐ SPOTLIGHTED
                  </div>
                )}

                {/* Aura Badge Overlay */}
                {auraBadge && (
                  <div className="aura-overlay">
                    <img src={auraBadge.emoji} alt={auraBadge.label} className="aura-badge-image" />
                  </div>
                )}

                {/* Swipe Indicators */}
                {swipeAnimation === 'swipe-right' && (
                  <div className="swipe-indicator fire">
                    <span className="indicator-emoji">🔥</span>
                    <span className="indicator-text">{fireWord.toUpperCase()}</span>
                  </div>
                )}
                {swipeAnimation === 'swipe-left' && (
                  <div className="swipe-indicator mid">
                    <span className="indicator-emoji">😐</span>
                    <span className="indicator-text">{midWord.toUpperCase()}</span>
                  </div>
                )}
              </div>

              <div className="swipe-info">
                <h2
                  className="swipe-username"
                  onClick={() => onNavigate && onNavigate('profile', currentAvatar.username)}
                  style={{ cursor: 'pointer' }}
                >
                  {currentAvatar.username}
                  {currentAvatar.isVerified && <span className="verified-badge" title="Verified Roblox Account">✓</span>}
                </h2>
                {auraDisplay && (
                  <div className="aura-info-compact">
                    <span className="aura-percent">{auraDisplay.percent} Aura</span>
                    <span className="aura-swipes">{auraDisplay.swipes} swipes</span>
                  </div>
                )}
                <p className="swipe-progress">
                  {currentIndex + 1} / {avatars.length}
                </p>
              </div>
            </div>

            {/* Swipe Buttons */}
            <div className="swipe-buttons">
              <button
                className="swipe-button mid-button"
                onClick={() => handleSwipe(false)}
                disabled={votingInProgress}
              >
                <span className="button-icon">😐</span>
                <span className="button-text">{midWord}</span>
                <span className="button-hint">←</span>
              </button>

              <button
                className="swipe-button fire-button"
                onClick={() => handleSwipe(true)}
                disabled={votingInProgress}
              >
                <span className="button-icon">🔥</span>
                <span className="button-text">{fireWord}</span>
                <span className="button-hint">→</span>
              </button>
            </div>

            <div className="swipe-instructions">
              <p>💡 Use arrow keys: ← {midWord} | {fireWord} →</p>
              <p style={{color: '#00ff00', fontWeight: 'bold'}}>🎉 FREE - No cost to swipe!</p>
            </div>
          </>
        )}
      </div>

      {/* Aura Stats Detailed */}
      {currentAvatar && displayAuraStats && (
        <div className="aura-stats">
          <div className="stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-label">Fire Votes</div>
            <div className="stat-value">{displayAuraStats.fireSwipes}</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💀</div>
            <div className="stat-label">L Votes</div>
            <div className="stat-value">{displayAuraStats.leftSwipes}</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-label">Net Aura</div>
            <div className="stat-value">{displayAuraStats.netAura}</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">{displayAuraStats.tier.emoji}</div>
            <div className="stat-label">Aura %</div>
            <div className="stat-value">{displayAuraStats.auraPercent}%</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuraFarming;
